import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Card, Spinner, Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLayerGroup,
  faUsers,
  faPlus,
  faGear,
} from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import {
  useGetGrowthWheelEditorSummariesQuery,
  useCreateGrowthWheelForGuildMutation,
} from '../../redux/growth-wheel.endpoints'
import { useGetAllRolesQuery } from '../../redux/role.endpoints'
import { GrowthWheelSummary } from '../../types/GrowthWheel'
import { User } from '../../types/User'

interface Props {
  user: User
}

const GrowthWheelEditorLanding = ({ user }: Props) => {
  const {
    data: wheels,
    isLoading,
    isError,
  } = useGetGrowthWheelEditorSummariesQuery()
  const { data: roles, isLoading: rolesLoading } = useGetAllRolesQuery()
  const [createWheel, { isLoading: createPending }] =
    useCreateGrowthWheelForGuildMutation()

  const [showAddDropdown, setShowAddDropdown] = useState(false)
  const [selectedGuildId, setSelectedGuildId] = useState('')
  const addDropdownRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()

  const assignedRoleIds = useMemo(
    () => new Set((wheels ?? []).map(w => w.roleId)),
    [wheels],
  )

  const guildDataReady = !rolesLoading && wheels !== undefined

  const availableGuilds = useMemo(() => {
    if (!guildDataReady || !roles) {
      return []
    }
    return roles
      .filter(g => !assignedRoleIds.has(g.id))
      .slice()
      .sort((a, b) => a.role.localeCompare(b.role))
  }, [guildDataReady, roles, assignedRoleIds])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        addDropdownRef.current &&
        !addDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAddDropdown(false)
        setSelectedGuildId('')
      }
    }
    if (showAddDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showAddDropdown])

  const handleEditWheel = (wheel: GrowthWheelSummary) => {
    navigate(`/edit-growth-wheel/${wheel.id}`)
  }

  const closeAddDropdown = () => {
    setShowAddDropdown(false)
    setSelectedGuildId('')
  }

  const openAddDropdown = () => {
    if (addButtonDisabled) return
    setSelectedGuildId('')
    setShowAddDropdown(true)
  }

  const toggleHeaderAddDropdown = () => {
    if (addButtonDisabled) return
    setSelectedGuildId('')
    setShowAddDropdown(prev => !prev)
  }

  const handleConfirmAdd = async () => {
    if (!selectedGuildId) return
    try {
      await createWheel({ roleId: Number(selectedGuildId) }).unwrap()
      toast.success('Growth Wheel created.')
      closeAddDropdown()
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err && err.data && typeof err.data === 'object'
          ? (err.data as Record<string, unknown>)
          : null
      const message =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message) ||
        'Could not create Growth Wheel.'
      toast.error(message, { toastId: 'create-gw-error' })
    }
  }

  const addButtonDisabled =
    !guildDataReady || availableGuilds.length === 0 || createPending

  const addDropdownContent = showAddDropdown ? (
    <div className="gw-editor-add-dropdown-panel">
      <h4 className="gw-editor-add-dropdown-title">Add Growth Wheel</h4>
      <p className="gw-editor-add-dropdown-help">
        Select a guild to create a new Growth Wheel for. Guilds that already
        have a Growth Wheel are not listed.
      </p>
      {!guildDataReady ? (
        <div className="d-flex justify-content-center py-3">
          <Spinner animation="border" size="sm" variant="secondary" />
        </div>
      ) : availableGuilds.length === 0 ? (
        <p className="gw-editor-add-dropdown-help mb-0">
          There are no guilds left without a Growth Wheel.
        </p>
      ) : (
        <Form.Select
          value={selectedGuildId}
          onChange={e => setSelectedGuildId(e.target.value)}
          className="text-secondary"
          autoFocus
        >
          <option value="">Select a guild…</option>
          {availableGuilds.map(guild => (
            <option key={guild.id} value={guild.id}>
              {guild.role}
            </option>
          ))}
        </Form.Select>
      )}
      <div className="gw-editor-add-dropdown-actions">
        <button
          type="button"
          className="gw-editor-add-dropdown-btn gw-editor-add-dropdown-btn--cancel"
          onClick={closeAddDropdown}
        >
          Cancel
        </button>
        <button
          type="button"
          className="gw-editor-add-dropdown-btn gw-editor-add-dropdown-btn--submit"
          onClick={handleConfirmAdd}
          disabled={
            !selectedGuildId ||
            createPending ||
            !guildDataReady ||
            availableGuilds.length === 0
          }
        >
          {createPending ? (
            <Spinner animation="border" size="sm" className="align-middle" />
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} />
              Add
            </>
          )}
        </button>
      </div>
    </div>
  ) : null

  if (isLoading) {
    return (
      <div className="gw-editor-landing">
        <div className="gw-editor-landing-loader">
          <Spinner animation="border" role="status" variant="secondary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="gw-editor-landing">
        <div className="gw-editor-landing-empty">
          <p className="gw-editor-landing-empty-title">
            Unable to load Growth Wheels
          </p>
          <p className="gw-editor-landing-empty-text">
            Please try again later or contact an administrator.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="gw-editor-landing">
      <div className="gw-editor-landing-header">
        <div>
          <h1 className="gw-editor-landing-title">Growth Wheel Management</h1>
          <p className="gw-editor-landing-subtitle">
            Select a guild to configure its competency framework and growth wheel
            structure.
          </p>
        </div>
        <div className="gw-editor-add-dropdown-wrap" ref={addDropdownRef}>
          <button
            type="button"
            className="gw-editor-landing-add-btn"
            onClick={toggleHeaderAddDropdown}
            disabled={addButtonDisabled}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Growth Wheel
          </button>
          {addDropdownContent}
        </div>
      </div>

      {!wheels || wheels.length === 0 ? (
        <div className="gw-editor-landing-empty">
          <div className="gw-editor-landing-empty-icon">
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <h2 className="gw-editor-landing-empty-title">
            No Growth Wheels yet
          </h2>
          <p className="gw-editor-landing-empty-text">
            Add a Growth Wheel for a guild to start building its competency
            framework.
          </p>
          <button
            type="button"
            className="gw-editor-landing-add-btn"
            onClick={openAddDropdown}
            disabled={addButtonDisabled}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add First Growth Wheel
          </button>
        </div>
      ) : (
        <div className="gw-editor-landing-grid">
          {wheels.map(wheel => {
            const areas = wheel.competenciesCount
            const subs = wheel.subCompetenciesCount

            return (
              <Card key={wheel.id} className="gw-editor-wheel-card">
                <button
                  type="button"
                  className="gw-editor-wheel-card-main"
                  onClick={() => handleEditWheel(wheel)}
                >
                  <h3 className="gw-editor-wheel-card-title">
                    {wheel.roleName}
                  </h3>

                  <div className="gw-editor-wheel-card-stats">
                    <div className="gw-editor-wheel-card-stat">
                      <FontAwesomeIcon
                        icon={faLayerGroup}
                        className="gw-editor-wheel-card-stat-icon"
                      />
                      <span>
                        {areas} {areas === 1 ? 'competency' : 'competencies'},{' '}
                        {subs}{' '}
                        {subs === 1 ? 'sub-competency' : 'sub-competencies'}
                      </span>
                    </div>
                  </div>

                  <div className="gw-editor-wheel-card-contributors">
                    <FontAwesomeIcon icon={faUsers} />
                    <span>
                      {wheel.contributorsCount}{' '}
                      {wheel.contributorsCount === 1
                        ? 'contributor'
                        : 'contributors'}
                    </span>
                  </div>
                </button>
                <div className="gw-editor-wheel-card-actions">
                  <button
                    type="button"
                    className="gw-editor-wheel-card-edit-btn"
                    onClick={() => handleEditWheel(wheel)}
                  >
                    <FontAwesomeIcon icon={faGear} />
                    Edit Wheel
                  </button>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default GrowthWheelEditorLanding
